import { useCallback, useEffect, useRef, useState } from 'react';
import { generateTransferOrderNumber } from 'services/transferOrders/api';
import { TransferOrderType } from 'services/transferOrders';
import { logErrorCtx } from 'app/logging';

interface UseTransferOrderNumberProps {
  sourceLocationId: number | null;
  destinationLocationId: number | null;
  isLocationSelectionComplete: boolean;
  transferType: TransferOrderType | null;
}

interface UseTransferOrderNumberResult {
  orderNumber: string;
  numericOrderNumber: string;
  isGenerating: boolean;
  error: string | null;
}

export const useTransferOrderNumber = ({
  sourceLocationId,
  destinationLocationId,
  isLocationSelectionComplete,
  transferType,
}: UseTransferOrderNumberProps): UseTransferOrderNumberResult => {
  const [orderNumber, setOrderNumber] = useState<string>('New Transfer Order');
  const [numericOrderNumber, setNumericOrderNumber] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequestIdRef = useRef(0);

  const generateNumber = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;
    // Show "New Transfer Order" until both locations are selected (per AC2.1)
    if (
      sourceLocationId == null ||
      destinationLocationId == null ||
      !isLocationSelectionComplete
    ) {
      setOrderNumber('New Transfer Order');
      setNumericOrderNumber('');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await generateTransferOrderNumber({
        sourceLocationId,
        destinationLocationId,
      });

      // Only update state if this is still the latest request
      if (requestId === latestRequestIdRef.current) {
        // Extract just the numeric part from the response
        const numericPart = response.number.replace(/^(MTO|STO)\s*/, '');

        setOrderNumber(response.number);
        setNumericOrderNumber(numericPart);
      }
    } catch (err) {
      const error = err as Error;
      logErrorCtx('Failed to generate transfer order number', {
        stackTrace: error.stack,
        error,
        description: `Source: ${sourceLocationId}, Destination: ${destinationLocationId}`,
        component: 'useTransferOrderNumber',
      });
      // Only update error state if this is still the latest request
      if (requestId === latestRequestIdRef.current) {
        setError('Failed to generate order number');

        // Fallback to temporary number based on detected type
        if (transferType) {
          setOrderNumber(`${transferType} XXXX`);
          setNumericOrderNumber('XXXX');
        } else {
          setOrderNumber('New Transfer Order');
          setNumericOrderNumber('');
        }
      }
    } finally {
      // Always stop loading for the latest request
      if (requestId === latestRequestIdRef.current) {
        setIsGenerating(false);
      }
    }
  }, [
    sourceLocationId,
    destinationLocationId,
    isLocationSelectionComplete,
    transferType,
  ]);

  useEffect(() => {
    generateNumber();
  }, [generateNumber]);

  return {
    orderNumber,
    numericOrderNumber,
    isGenerating,
    error,
  };
};
